<template>
  <Head>
    <meta
      v-if="published"
      name="robots"
      content="all"
    >
    <meta
      v-else
      name="robots"
      content="none"
    >

    <meta
      property="og:title"
      :content="title"
    />
    <meta
      property="og:type"
      content="website"
    />
    <meta
      property="og:image"
      :content="coverImageUrl"
    />
  </Head>
  <div class="checkout-screen">
    <div
      class="flex flex-col overflow-x-hidden flex-grow pb-32 lg:pb-0"
      :class="{ 'overflow-hidden': cartInteracting }"
    >
      <CheckoutHeader current-screen="tickets" />
      <main class="checkout-main">
        <EventDetails
          v-if="event"
          :event="event"
          :hide-tickets="presalePasswordRequired"
        />
        <Transition>
          <AccessCodeInput
            v-if="showAccessCodeInput"
            :key="`aci-${aciCounter}`"
            :title="$gettext('Access code')"
            :instructions="$gettext('Have an access code? Enter it here.')"
            :placeholder="$gettext('Access code')"
            :accessible-label="$gettext('Add access code')"
            @apply="onApplyAccessCode"
          />
        </Transition>
        <Transition>
          <AccessCodeInput
            v-if="presalePasswordRequired"
            key="presale-password-input"
            :title="$gettext('Presale Password')"
            :instructions="$gettext('This presale requires a password.')"
            :placeholder="$gettext('Password')"
            :accessible-label="$gettext('Enter presale password')"
            variant="password"
            @apply="onApplyAccessCode"
          />
        </Transition>
        <div
          v-if="event?.footerContent"
          class="footer"
        >
          <MinimumAgeDisplay
            :minimum-age="minimumAge"
            variant="footer"
          />
          <div class="flex items-start gap-2">
            <iconify-icon
              v-if="event?.footerContent"
              icon="mdi:information-outline"
              class="text-lg font-light text-vp-base-600"
            />
            <div
              class="footer-content"
              v-html="sanitizeHtml(event.footerContent)"
            />
          </div>
        </div>
      </main>
    </div>
    <section class="px-3 lg:px-0 lg:sticky lg:top-0">
      <div class="cart shopping-cart px-3 overflow-x-auto lg:overflow-x-hidden">
        <ShoppingCart
          v-if="event && shoppingCart"
          show-reset
          :event="event"
          :items="shoppingCartItems"
          :price-breakdown="priceBreakdown"
          :control-quantity="shoppingCart.controlQuantity"
          class="min-w-full"
        >
          <template #footer>
            <div v-if="checkoutStore.hasValidLock && priceBreakdown.orderTotal > 0">
              <StripePaymentForm
                hide-form
                hide-submit
                :amount="priceBreakdown.orderTotal"
                :hide-checkboxes="['terms_of_service']"
                :quick-checkout-tos="event.config?.quickCheckoutTos"
                @quick-checkout-load="onQuickCheckoutLoad"
              >
                <template #footer>
                  <div class="my-2 sm:my-4 flex justify-center items-center gap-4 text-vp-base-400">
                    <hr class="grow border">
                    <span class="uppercase">{{ $gettext('or') }}</span>
                    <hr class="grow border">
                  </div>
                </template>
              </StripePaymentForm>
            </div>

            <ShoppingCartFooter
              show-next
              :next-label="nextLabel"
              @next="onNext"
            />
          </template>
        </ShoppingCart>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import {
  CaptchaVerificationError,
  InvalidRecaptchaTokenError,
  TicketLockingError,
  useCheckoutStore,
} from '@/stores/checkout';
import { computed, onMounted, onServerPrefetch, ref } from 'vue';
import EventDetails from '@/checkout/EventDetails.vue';
import ShoppingCart from '@/checkout/ShoppingCart.vue';
import { Checkout } from '@vp/common';
import { useGettext } from 'vue3-gettext';
import { useToast } from 'vue-toastification';
import { useRecaptcha } from '@/components/recaptcha/useRecaptcha';
import ShoppingCartFooter from '@/checkout/ShoppingCartFooter.vue';
import { useRouter } from 'vue-router';
import { navigate } from 'vike/client/router';
import { useCheckoutTitle } from '@/checkout/useCheckoutTitle';
import { useConfig } from 'vike-vue/useConfig';
import { redirect, render } from 'vike/abort';
import { useRootStore } from '@/stores/root';
import { Head } from 'vike-vue/Head';
import { usePageContext } from 'vike-vue/usePageContext';
import { initEventId } from '@/checkout/utils/initEventId';
import { NotFoundError } from '@/errors/NotFoundError';
import sanitizeHtml from 'sanitize-html';
import StripePaymentForm from '@/checkout/StripePaymentForm.vue';
import MinimumAgeDisplay from '@/checkout/MinimumAgeDisplay.vue';
import { useEventImage } from '@/composables/useEventImage';
import CheckoutHeader from '@/checkout/CheckoutHeader.vue';
import * as VPErrorLegacy from '@/types/VPError';
import { useTracking } from '@/components/tracking/useTracking';
import AccessCodeInput from '@/checkout/AccessCodeInput.vue';

const rootStore = useRootStore();
const checkoutStore = useCheckoutStore();
const { $gettext } = useGettext();
const toast = useToast();
const router = useRouter();
const { title } = useCheckoutTitle();
const pageContext = usePageContext();
const { trackEvent } = useTracking();

const cartInteracting = ref(false);
const quickCheckoutAvailable = ref(false);

const presalePasswordRequired = ref(false);
const aciCounter = ref(0);

const onQuickCheckoutLoad = () => quickCheckoutAvailable.value = true;

onServerPrefetch(async () => {
  const config = useConfig();
  try {
    await checkoutStore.init();
    config({
      title: title.value,
    });
  } catch (e) {
    if (e instanceof NotFoundError) {
      throw render(404, $gettext('Event not found'));
    }

    if (e.message === 'Unavailable') {
      const url = new URL(`/events/${checkoutStore.getSelectedEventId}/orders/new`, rootStore.apiBase);
      throw redirect(url.href);
    }
    console.error('caught in prefetch', e);
    throw e;
  }
});

const recaptcha = useRecaptcha({
  autoInit: true,
  initWatcher: () => checkoutStore.recaptchaEnabled,
});

onMounted(async () => {
  // TODO: Needs to be set differently
  initEventId(pageContext);
  await checkoutStore.init();

  if (pageContext.urlParsed.search.presaleName) {
    applyAccessCode(pageContext.urlParsed.search.presaleName);
  }
  checkoutStore.validateNotABot(recaptcha);
});

const event = computed(() => checkoutStore.getSelectedEvent);

const coverImageUrl = useEventImage(event);

const shoppingCart = computed(() => checkoutStore.shoppingCart);
const shoppingCartItems = computed(() => checkoutStore.getCartedTickets.filter(({ count }) => count > 0));
const priceBreakdown = computed<Checkout.PriceBreakdown.PriceBreakdown>(() => shoppingCart.value?.priceBreakdown);
const published = computed(() => checkoutStore.getEventCheckoutConfig?.published);

const showAccessCodeInput = computed(() => !presalePasswordRequired.value && checkoutStore.getEventCheckoutConfig?.accessCode);

const minimumAge = computed(() => event.value.minimumAge);

const nextLabel = computed(() => (priceBreakdown.value?.orderTotal || 0) && quickCheckoutAvailable.value > 0 ? $gettext('Pay another way') : $gettext('Checkout'));

const applyAccessCode = async (code: string) => {
  const presale = Checkout.ShoppingCartItemInput.newPresale(code);
  try {
    await checkoutStore.storeShoppingCartItem(presale);
    trackEvent('access_code_applied');
    toast.success($gettext('Access code applied'));
    aciCounter.value++;
    checkoutStore.fetchAvailableTickets({ force: true });
    presalePasswordRequired.value = false;

    if (typeof window !== 'undefined') {
      const targetUrl = new URL(window.location.href);
      targetUrl.searchParams.delete('presaleName');
      window.history.replaceState(null, '', targetUrl.href);
    }
  } catch (e) {
    if ((e as VPErrorLegacy).type == 'VPError') {
      const vpError = e as VPErrorLegacy;
      for (const msg of vpError.errors) {
        toast.error(msg);
      }
    } else if (e instanceof Checkout.ShoppingCartError && e.code === 'PASSWORD_REQUIRED') {
      presalePasswordRequired.value = true;
      toast.info($gettext('This presale requires a password'));
      if (typeof window !== 'undefined') {
        const targetUrl = new URL(window.location.href);
        targetUrl.searchParams.delete('presaleName');
        window.history.replaceState(null, '', targetUrl.href);
      }
    } else {
      toast.error($gettext('Error applying access code'));
      throw e;
    }
  }
};

const onApplyAccessCode = (_code: string) => {
  const code = _code.trim();
  if (!code || code == '') {
    return;
  }

  applyAccessCode(code);
};

const onNext = async () => {
  try {
    await checkoutStore.acquireLock(recaptcha);
    await navigate(router.resolve({ name: 'OrderPayment', params: { eventId: event.value.slug } }).href);
  } catch (e) {
    if (e instanceof TicketLockingError) {
      alert($gettext('Some of the tickets you requested could not be reserved!'));
    } else if (e instanceof CaptchaVerificationError) {
      alert($gettext("Please prove you're not a robot"));
    } else if (e instanceof InvalidRecaptchaTokenError) {
      alert(
        `${$gettext('Unable to check recaptcha.')} ${$gettext('Please verify your ad-blocking software is not blocking Google Recaptcha.')}`,
      );
    } else {
      console.error('acquire lock error:', e);
      throw e;
    }
  }
};
</script>
